import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Form from 'src/common/Form';
import Button from 'src/common/Button';
import Input from 'src/common/Input';
import Modal from 'src/common/Modal';
import styles from './index.module.scss';
import {
  useUpdateFolder,
  useUpdateFile,
  useCurrentFolderContext,
  getUpdateCacheOnFolderRename,
  getUpdateCacheOnFileRename,
  useLoadDocuments
} from 'src/models/documentsLibrary/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-light-svg-icons';
import { DocsNodeType } from 'src/models/documentsLibrary/types';
import { FOLDER_NAME_PATTERN, FILE_NAME_PATTERN, IDocumentsTableProps, FileStatus } from '../../types';
import { splitFileExt } from 'src/helpers/files';

interface IFormState {
  name: string;
}

interface IProps {
  docsNode: IDocumentsTableProps;
}

export const DocumentsRename: FC<IProps> = props => {
  const { docsNode } = props;
  const { nodeType, id, name, createDateTime, createdBy, childNodes, getUrl, attachedTo, status } = docsNode;
  const { t } = useTranslation();
  const currentFolder = useCurrentFolderContext();
  const [renameFolder] = useUpdateFolder();
  const [renameFile] = useUpdateFile();
  const { data: currentDocs } = useLoadDocuments(currentFolder.id);

  const [renameVisible, setRenameVisible] = useState(false);

  const isFolder = useMemo(() => {
    return nodeType === DocsNodeType.FOLDER;
  }, [nodeType]);

  const [fileName, fileExt] = splitFileExt(name);

  const [form] = Form.useForm();

  const initialValues = useMemo((): IFormState => {
    return {
      name: isFolder ? name : fileName
    };
  }, [fileName, isFolder, name]);

  const onOpen = useCallback(() => {
    setRenameVisible(true);
  }, []);
  const onClose = useCallback(() => {
    form.resetFields();
    setRenameVisible(false);
  }, [form]);

  const folders = useMemo(() => {
    return currentDocs?.filter(doc => doc.nodeType === DocsNodeType.FOLDER && doc.id !== id);
  }, [currentDocs, id]);

  const files = useMemo(() => {
    return currentDocs?.filter(doc => doc.nodeType === DocsNodeType.FILE && doc.id !== id);
  }, [currentDocs, id]);

  const onRenameFolder = useCallback(
    (values: IFormState) => {
      const { name: folderName } = values;
      const sameName = !!folders?.some(folder => folder.name === folderName);
      if (sameName) {
        form.setFields([
          {
            name: 'name',
            errors: [t('DocumentLibrary.existingFolderNameError')]
          }
        ]);
      } else {
        renameFolder({
          variables: { id: id, parentId: currentFolder.id, name: folderName },
          update: getUpdateCacheOnFolderRename(id, folderName, currentFolder.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateFolder: {
              __typename: 'DirectoryNode',
              id,
              name: folderName,
              changedAt: '',
              createDateTime,
              createdBy: createdBy
                ? {
                    email: createdBy.email,
                    name: createdBy.name ?? '',
                    lastName: createdBy.lastName ?? '',
                    color: createdBy.color ?? null,
                    enabled: true,
                    __typename: 'User'
                  }
                : null,
              nodeType,
              children: childNodes ?? []
            }
          }
        }).catch(() => {
          form.setFields([
            {
              name: 'name',
              errors: [t('Common.unknownErrorDesc')]
            }
          ]);
        });
        onClose();
      }
    },
    [folders, form, t, renameFolder, id, currentFolder.id, createDateTime, createdBy, nodeType, childNodes, onClose]
  );

  const onRenameFile = useCallback(
    (values: IFormState) => {
      const { name: updatedFileName } = values;
      const fileName = fileExt ? `${updatedFileName}.${fileExt}` : updatedFileName;
      const sameName = files?.some(file => file.name === fileName);
      if (sameName) {
        form.setFields([
          {
            name: 'name',
            errors: [t('DocumentLibrary.existingFileNameError')]
          }
        ]);
      } else {
        renameFile({
          variables: { id: id, parentId: currentFolder.id, name: fileName },
          update: getUpdateCacheOnFileRename(id, fileName, currentFolder.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateFile: {
              __typename: 'FileNode',
              id,
              name: fileName,
              changedAt: '',
              createDateTime,
              createdBy: createdBy
                ? {
                    email: createdBy.email,
                    name: createdBy.name ?? '',
                    lastName: createdBy.lastName ?? '',
                    color: createdBy.color ?? null,
                    enabled: true,
                    __typename: 'User'
                  }
                : null,
              nodeType,
              putUrl: '',
              getUrl: getUrl ?? '',
              attachedTo: attachedTo ?? [],
              status: status ?? FileStatus.Done
            }
          }
        }).catch(() => {
          form.setFields([
            {
              name: 'name',
              errors: [t('Common.unknownErrorDesc')]
            }
          ]);
        });
        onClose();
      }
    },
    [
      fileExt,
      files,
      form,
      t,
      renameFile,
      id,
      currentFolder.id,
      createDateTime,
      createdBy,
      nodeType,
      getUrl,
      attachedTo,
      status,
      onClose
    ]
  );

  const onSubmit = useCallback(
    (values: IFormState) => {
      isFolder ? onRenameFolder(values) : onRenameFile(values);
    },
    [isFolder, onRenameFile, onRenameFolder]
  );

  const onKeyPress = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  return (
    <>
      <Button type={'text'} onClick={onOpen} icon={<FontAwesomeIcon icon={faPen} />} />
      {renameVisible && (
        <Modal
          title={<span>{t(isFolder ? 'DocumentLibrary.renameFolder' : 'DocumentLibrary.renameFile')}</span>}
          visible
          onCancel={onClose}
          maskClosable
          width={500}
          footer={null}
          keyboard={false}
        >
          <Form
            initialValues={initialValues}
            onFinish={onSubmit}
            form={form}
            layout={'vertical'}
            className={styles.form}
            onKeyDown={e => onKeyPress(e)}
            onKeyUp={e => onKeyPress(e)}
          >
            <Form.Item
              name={'name'}
              label={t(isFolder ? 'DocumentLibrary.folderName' : 'DocumentLibrary.fileName')}
              rules={[
                { required: true, message: t('DocumentLibrary.folderNameRequired') },
                {
                  pattern: new RegExp(isFolder ? FOLDER_NAME_PATTERN : FILE_NAME_PATTERN),
                  message: t(isFolder ? 'DocumentLibrary.folderNamePolicy' : 'DocumentLibrary.fileNamePolicy')
                }
              ]}
            >
              <Input
                type={'text'}
                placeholder={t(isFolder ? 'DocumentLibrary.folderName' : 'DocumentLibrary.fileName')}
                onPressEnter={() => onSubmit(form.getFieldsValue())}
              />
            </Form.Item>
            <div className={styles.formFooter}>
              <Button type={'text'} onClick={onClose}>
                {t('Common.cancel')}
              </Button>
              <Button type={'primary'} htmlType={'submit'}>
                {t('Common.save')}
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default DocumentsRename;
